import { Avatar, Divider, Header, Menu, MenuItem, MenuSection, Text } from "@hopper-ui/components";
import { PlusIcon } from "@hopper-ui/icons";
import { useWorkspaceContext } from "../../../WorkspaceContext.tsx";
import { useMemo } from "react";
import { useShellTranslation } from "../../../i18n/i18next.ts";
import { cleanupDisplayName } from "./cleanupDisplayName.ts";

interface WorkspaceSwitcherMenuProps {
    onCreateWorkspace?: () => void;
}

export function WorkspaceSwitcherMenu({ onCreateWorkspace }: WorkspaceSwitcherMenuProps) {
    const { t } = useShellTranslation();
    const { changeWorkspace, currentWorkspace, workspaces } = useWorkspaceContext();

    const { sortedWorkspaces, partnerWorkspaces, clientWorkspaces, hasPartner } = useMemo(() => {
        const sorted = workspaces.toSorted((a, b) => a.name.localeCompare(b.name));
        const partners = sorted.filter(ws => ws.isPartner);
        const clients = sorted.filter(ws => !ws.isPartner);
        return {
            sortedWorkspaces: sorted,
            partnerWorkspaces: partners,
            clientWorkspaces: clients,
            hasPartner: partners.length > 0
        };
    }, [workspaces]);

    return (
        <Menu selectedKeys={currentWorkspace ? [currentWorkspace.id] : []} selectionMode="single">
            {hasPartner ? (
                <>
                    <MenuSection>
                        <Header>{t("WorkspaceSwitcher.yourWorkspaces", { count: partnerWorkspaces.length })}</Header>
                        {partnerWorkspaces.map(ws => (
                            <MenuItem id={ws.id} key={ws.id} onPress={() => changeWorkspace(ws)}>
                                <Avatar name={cleanupDisplayName(ws.name)} borderRadius="rounded-md" />
                                <Text size="sm" overflow="hidden" whiteSpace="nowrap" textOverflow="ellipsis">
                                    {ws.name}
                                </Text>
                            </MenuItem>
                        ))}
                    </MenuSection>
                    {clientWorkspaces.length > 0 && (
                        <MenuSection>
                            <Header>{t("WorkspaceSwitcher.clientWorkspaces")}</Header>
                            {clientWorkspaces.map(ws => (
                                <MenuItem id={ws.id} key={ws.id} onPress={() => changeWorkspace(ws)}>
                                    <Avatar name={cleanupDisplayName(ws.name)} borderRadius="rounded-md" />
                                    <Text size="sm" overflow="hidden" whiteSpace="nowrap" textOverflow="ellipsis">
                                        {ws.name}
                                    </Text>
                                </MenuItem>
                            ))}
                        </MenuSection>
                    )}
                </>
            ) : (
                <MenuSection>
                    <Header>{t("WorkspaceSwitcherMenu.workspaces")}</Header>
                    {sortedWorkspaces.map(ws => (
                        <MenuItem id={ws.id} key={ws.id} onPress={() => changeWorkspace(ws)}>
                            <Avatar name={cleanupDisplayName(ws.name)} borderRadius="rounded-md" />
                            <Text size="sm" overflow="hidden" whiteSpace="nowrap" textOverflow="ellipsis">
                                {ws.name}
                            </Text>
                        </MenuItem>
                    ))}
                </MenuSection>
            )}
            {onCreateWorkspace && (
                <>
                    <Divider />
                    <MenuItem id="create-workspace" onPress={onCreateWorkspace}>
                        <PlusIcon />
                        <Text size="sm">{t("WorkspaceSwitcher.newWorkspace")}</Text>
                    </MenuItem>
                </>
            )}
        </Menu>
    );
}
